import { useCallback, useMemo } from 'react';
import { EntityForm } from '../form.types';
import { ModuleSettingsForm } from './form';
import { ModuleSettingsFormOutput } from './form-output';
import { ModuleSettingsFormSchema } from './form-schema';
import { moduleSettingsFormUISchema } from './ui-schema';
import { useQueryGetModule, useMutationUpdateModule } from '../../../modules/modules.queries';
import { useHasUIFeatures } from '../../../../core/permissions/has-permissions.hook';
import { useQueryGetHubDetails } from '../../../lockers/hubs.queries';
import { HubLayoutPreview } from '../shared/hub-layout-preview.component';

export function useEditModuleForm(
  moduleID?: string,
  hubID?: string,
  moduleDisplayName?: string,
  _formData?: ModuleSettingsForm
): EntityForm | null {
  const moduleResult = useQueryGetModule(moduleID);
  const moduleMutation = useMutationUpdateModule();
  const { hasAllFeatures: canEdit } = useHasUIFeatures(['internal-groundfloor']);
  const hubDetailsResult = useQueryGetHubDetails(hubID);

  const defaultValues = useMemo(() => {
    if (!moduleResult.data) return {} as ModuleSettingsForm;
    return ModuleSettingsFormOutput.FromModuleDto(moduleResult.data);
  }, [moduleResult.data]);

  const initialFormOutput = useMemo(() => {
    return new ModuleSettingsFormOutput(defaultValues);
  }, [defaultValues]);

  const formSchema = useMemo(() => ModuleSettingsFormSchema.Build(), []);

  const onSubmit = useCallback(
    async (formOutput: ModuleSettingsFormOutput) => {
      if (!moduleID) return;
      const result = await moduleMutation.mutateAsync({
        moduleID,
        dto: ModuleSettingsFormOutput.ToEditModuleDto(formOutput)
      });
      await moduleResult.refetch();
      return { entityId: result.id };
    },
    [moduleID, moduleMutation, moduleResult]
  );

  const componentToRenderBelowForm = useMemo(() => {
    if (!hubID || !hubDetailsResult.data?.towers) return undefined;
    const towers = hubDetailsResult.data.towers.map((t) => ({
      model: t.modelName,
      inventoryID: t.inventoryID ?? ''
    }));
    const { name, type } = hubDetailsResult.data;
    return function HubLayoutPreviewWrapper() {
      return <HubLayoutPreview towers={towers} hubName={name} hubType={type} />;
    };
  }, [hubID, hubDetailsResult.data]);

  if (!moduleID) return null;
  if (!formSchema) return null;

  return {
    isLoading: moduleResult.isLoading,
    title: moduleDisplayName ?? 'Module Settings',
    subtitle: moduleID,
    allowSubtitleCopy: true,
    defaultValues,
    initialFormOutput,
    formSchema,
    uiSchema: moduleSettingsFormUISchema,
    getOutput(data) {
      return new ModuleSettingsFormOutput(data as ModuleSettingsForm);
    },
    onSubmit,
    componentToRenderBelowForm,
    isLocked: !canEdit,
    lockedMessage: canEdit ? null : 'Editing module settings requires internal Groundfloor access'
  };
}
